import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RuleGroupType } from "react-querybuilder";
import BuildFormulaquerygenerater from "../others/BuildFormulaquerygenerater";
import { apiCall } from "../main/ApiCall";
import "../css/others/FilterPage.css";
import "../css/others/Table.css";
import ToastPopup from "../popups/ToastPopup";
import { DropdownOption, createToastMessage, ToastMessage } from "../interFaces/interfaces";
import EncryptionUtils from "../main/encryptionUtils";
const BuildFormula: React.FC = () => {
  const [attributes, setAttributes] = useState<{ name: string; label: string; type: string }[]>([]);
  const [query, setQuery] = useState<RuleGroupType>({ combinator: "and", rules: [] });
  const [, setExistingFilters] = useState<any[]>([]);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [auditFrequency, setAuditFrequency] = useState<string>("");
  const [meetingDuration, setMeetingDuration] = useState<string>("");
  const [isDisabled, setIsDisabled] = useState(false);
  const [extensionDays, setExtensionDays] = useState<string>("");
  const [campaignQcReviewDuration, setCampaignQcReviewDuration] = useState<string>("");
  const [, setExistingAuditConfig] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);
  const [dropdownOptions, setDropdownOptions] = useState<DropdownOption[]>([]);
  const [dateDropdownValues, setDateDropdownValues] = useState<number[]>([15, 30, 45]);
  const navigate = useNavigate();
  const clientId = localStorage.getItem("clientId");
  const email = localStorage.getItem("email");
  const [fields, setFields] = useState<any>({});
  const [questionText, setQuestionText] = useState<Record<string, string>>({});
  useEffect(() => {
    const permissions = EncryptionUtils.get<any>("permissions", null);
    if (permissions.tabs["buildFormula"]) {
      setFields(permissions.tabs["buildFormula"].fields);
    } else {
      console.log("No permissions found for 'buildFormula' tab");
    }
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [attributesRes, filtersRes, configRes, dropdownOptionsRes, normalDateRes] = await Promise.all([
          apiCall("get", `/api/client-apis?clientId=${clientId}`),
          apiCall("get", `/api/client-mappings/default-filters?clientId=${clientId}`),
          apiCall("get", `/api/client-config/${clientId}`),
          apiCall("get", `/api/build-formula/dropdown-config`),
          apiCall("get", `/api/dropdown-config/normal`)
        ]);
        setDropdownOptions(dropdownOptionsRes);
        if (normalDateRes && normalDateRes.values && normalDateRes.values.length > 0) {
          setDateDropdownValues(normalDateRes.values);
        }
        if (dropdownOptionsRes && Array.isArray(dropdownOptionsRes)) {
          const newQuestionText: Record<string, string> = {};
          dropdownOptionsRes.forEach((option: DropdownOption) => {
            if (option.question && option.category) {
              newQuestionText[option.category] = option.question;
            }
          });
          setQuestionText(newQuestionText);
        }
        const fieldsObject = attributesRes.attributeToLabelMapping;
        const mandatoryFields = ["Team Size", "Project Category", "Billing Type", "Target ML", "Project Start Date", "Project End Date"];
        const mappedAttributes = Object.entries(fieldsObject)
          .filter(([key]) => mandatoryFields.includes(key))
          .map(([key, label]) => ({ name: label as string, label: key, type: key.toLowerCase().includes("date") ? "date" : "string" }));
        setAttributes(mappedAttributes);
        const filters = filtersRes.filters || [];
        setExistingFilters(filters);
        const transformedFilters = filters.map((filter: any) => ({ field: filter.field, operator: filter.operator, value: filter.value }));
        setQuery({ combinator: "and", rules: transformedFilters });
        setIsDisabled(!!filtersRes.isDisabled);
        if (configRes) {
          if (configRes.projectPickUpIntervel) setAuditFrequency(String(configRes.projectPickUpIntervel));
          if (configRes.meetingDuration) setMeetingDuration(String(configRes.meetingDuration));
          if (configRes.campaignQcReviewDuration) setCampaignQcReviewDuration(String(configRes.campaignQcReviewDuration));
          if (configRes.extensionDays) setExtensionDays(String(configRes.extensionDays));
          setExistingAuditConfig(true);
        }
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message;
        setMessage(createToastMessage(errorMessage, "error", 3000));
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [clientId]);
  const getDropdownValues = (category: string): number[] => {
    const option = dropdownOptions.find(opt => opt.category === category);
    return option ? option.values : [];
  };
  const getDropdownQuestion = (category: string): string => {
    return questionText[category] || "";
  };
  const renderDropdownOptions = (values: number[], unit: string) => {
    return values.map(value => (
      <option key={value} value={value}>{value} {unit}</option>
    ));
  };
  const getUnitForCategory = (category: string): string => {
    switch (category) {
      case "audit_frequency_options":
        return "Days";
      case "audit_meeting_duration":
        return "Min";
      case "campaign_review_by_qc":
        return "Days";
      case "report_finalization_after_campaign":
        return "Days";
      default:
        return "";
    }
  };
  const validateAuditQuestions = (): boolean => {
    const auditCategories = [{ category: "audit_frequency_options", value: auditFrequency, label: "Audit Frequency" }, { category: "audit_meeting_duration", value: meetingDuration, label: "Audit Meeting Duration" }, { category: "campaign_review_by_qc", value: campaignQcReviewDuration, label: "Campaign Review by QC" }, { category: "report_finalization_after_campaign", value: extensionDays, label: "Report Finalization After Campaign" }];
    const unansweredQuestions = auditCategories.filter(item => {
      const question = getDropdownQuestion(item.category);
      return question && !item.value;
    });
    if (unansweredQuestions.length > 0) {
      const questionList = unansweredQuestions
        .map(item => getDropdownQuestion(item.category))
        .join(", ");
      setMessage(createToastMessage(`Please answer the following questions before saving: ${questionList}`, "warning", 2500));
      return false;
    }
    return true;
  };
  const handleShowResults = async () => {
    if (isLoading) return;
    if (!validateAuditQuestions()) {
      return;
    }
    if (query.rules.length === 0) {
      setMessage(createToastMessage("Please add filters before proceeding.", "warning", 3000));
      return;
    }
    const transformFieldName = (fieldName: string): string => {
      return fieldName
        .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) =>
          index === 0 ? match.toLowerCase() : match.toUpperCase()
        )
        .replace(/\s+/g, "");
    };
    const validRules = query.rules.filter(
      (rule) => "field" in rule && "operator" in rule &&
        rule.value !== undefined && rule.value !== null && rule.value !== "" &&
        !(rule.field === "Project Category" && rule.value === "0")
    );
    const queryParams = validRules.map((rule) => {
      if ("field" in rule && "operator" in rule && "value" in rule) {
        const transformedField = transformFieldName(rule.field);
        let value = rule.value;
        return { field: transformedField, operator: rule.operator, value };
      }
      return null;
    }).filter(Boolean);
    if (queryParams.length === 0) {
      setMessage(createToastMessage("Please add filters with valid values before proceeding.", "warning", 3000));
      return;
    }
    const requestBody = { clientId, filters: queryParams, isDisabled: true, createdBy: email };
    const requestBodyFlag = { clientId, isDisabled: true };
    try {
      setIsLoading(true);
      await apiCall("post", `/api/client-mappings/default-filters`, requestBody);
      await apiCall("patch", `/api/client-mappings/default-filters`, requestBodyFlag);
      const auditRequestBody = { clientId, projectPickUpIntervel: parseInt(auditFrequency), meetingDuration: parseInt(meetingDuration), campaignQcReviewDuration: parseInt(campaignQcReviewDuration), extensionDays: parseInt(extensionDays), createdBy: email };
      await apiCall("post", "/api/client-config", auditRequestBody);
      setMessage(createToastMessage("Filters applied successfully, go to PROJECT LIST for further proceedings!", "success", 3000));
      setTimeout(() => window.location.reload(), 3000);
    } catch (error: any) {
      setMessage(createToastMessage("Error applying filters or audit configuration", "error", 3000));
    } finally {
      setIsLoading(false);
    }
  };
  const handleResetFilters = () => { setQuery({ combinator: "and", rules: [] }) };
  const renderAuditQuestion = (category: string, value: string, onChange: (value: string) => void) => {
    const question = getDropdownQuestion(category);
    const unit = getUnitForCategory(category);
    const values = getDropdownValues(category);
    if (!question) return null;
    return (
      <div className="filter-fields1">
        <div className="display-screen1">
          <p className="filter-header-p">{question}</p>
          <select className="audit-frequency-dropdown" value={value} onChange={(e) => onChange(e.target.value)} disabled={isDisabled}>
            <option value="">Select</option>{renderDropdownOptions(values, unit)}
          </select>
        </div>
      </div>
    );
  };
  const handleBack = () => { navigate(-1) };
  return (
    <div className={`filter-container ${isDisabled ? "disabled-overlay" : ""}`}>
      <div className="filter-container-box">
        <h2 className="filter-title">Build Formula</h2>
      </div>
      <div className="filter-fields">
        <div className="filter-fields-box">
          <p className="filter-header-p">Select your filters</p>
          {fields.edit && (
            <button className="button-reset" onClick={handleResetFilters}>Reset Filters</button>
          )}
        </div>
        <BuildFormulaquerygenerater attributes={attributes} onQueryChange={setQuery} query={query} disabled={isDisabled} dateDropdownValues={dateDropdownValues} />
      </div>
      {renderAuditQuestion("audit_frequency_options", auditFrequency, setAuditFrequency)}
      {renderAuditQuestion("audit_meeting_duration", meetingDuration, setMeetingDuration)}
      {renderAuditQuestion("campaign_review_by_qc", campaignQcReviewDuration, setCampaignQcReviewDuration)}
      {renderAuditQuestion("report_finalization_after_campaign", extensionDays, setExtensionDays)}
      <div className="button-container">
        <button className="button-back" onClick={handleBack}>Back</button>
        {fields.create && (
          <button className="button-show-results" onClick={handleShowResults} disabled={isDisabled || isLoading}> {isLoading ? "Processing..." : "Save"} </button>
        )}
      </div>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />
      )}
    </div>
  );
};
export default BuildFormula;